import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Bookmark,
  CheckCircle2,
  Clock,
  Eye,
  FileText,
  Loader2,
  LogOut,
  Send,
  ShieldAlert,
  WifiOff,
} from 'lucide-react';

import type { AnswerPayload } from '../../shared/protocol';
import type { ExamPublicInfo, ExamState } from '../../shared/exams';
import { api, ApiError } from '../lib/api';
import { examApi, examTransport, forgetAttempt, saveAttempt, savedAttempt, type SavedAttempt } from '../lib/exams';
import { startRecording, watchCheatSignals } from '../lib/recorder';
import { MonitorConsent } from './MonitorConsent';
import { MultipleChoiceQuestion } from './questions/MultipleChoiceQuestion';
import { TrueFalseQuestion } from './questions/TrueFalseQuestion';
import { MultiSelectQuestion } from './questions/MultiSelectQuestion';
import { FillBlankQuestion } from './questions/FillBlankQuestion';
import { MatchPairsQuestion, type PairLink } from './questions/MatchPairsQuestion';

/**
 * Hacer un examen.
 *
 * Código → nombre y apellido (sin cuenta) → preguntas. Se puede ir a cualquier
 * pregunta, volver y cambiar lo contestado; cada cambio se guarda en el
 * servidor al momento, así que recargar o perder la red no borra nada. El
 * reloj es el del servidor y, al llegar a cero, el examen se entrega solo.
 */
export const ExamTake: React.FC<{
  initialCode?: string | null;
  defaultName?: { firstName: string; lastName: string } | null;
  onExit: () => void;
}> = ({ initialCode, defaultName, onExit }) => {
  const [code, setCode] = useState(initialCode ?? '');
  const [info, setInfo] = useState<ExamPublicInfo | null>(null);
  const [attempt, setAttempt] = useState<SavedAttempt | null>(null);
  const [state, setState] = useState<ExamState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const lookup = useCallback(async (raw: string) => {
    const clean = raw.replace(/\D/g, '');
    if (clean.length !== 6) return;
    setBusy(true);
    setError('');
    try {
      // Si este navegador ya empezó el examen, se retoma donde iba.
      const saved = savedAttempt(clean);
      if (saved) {
        try {
          const s = await examApi.state(saved);
          setAttempt(saved);
          setState(s);
          return;
        } catch (err) {
          if (err instanceof ApiError && err.status === 404) forgetAttempt(clean);
          else throw err;
        }
      }
      const data = await api.post<{ exam: ExamPublicInfo }>('/exam-access/lookup', { code: clean });
      setInfo(data.exam);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo abrir el examen.');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (initialCode) void lookup(initialCode);
  }, [initialCode, lookup]);

  const begin = async (firstName: string, lastName: string, consent: boolean) => {
    if (!info) return;
    setBusy(true);
    setError('');
    try {
      const data = await examApi.start({ code: info.code, firstName, lastName, consent });
      const saved = { attemptId: data.state.attemptId, token: data.token };
      saveAttempt(info.code, saved);
      setAttempt(saved);
      setState(data.state);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo empezar el examen.');
    } finally {
      setBusy(false);
    }
  };

  if (state && attempt) {
    return state.finished ? (
      <ExamFinished state={state} onExit={onExit} />
    ) : (
      <ExamRunner attempt={attempt} initial={state} onFinished={setState} />
    );
  }

  if (info) {
    return (
      <ExamEntry
        info={info}
        busy={busy}
        error={error}
        defaultName={defaultName}
        onBegin={begin}
        onBack={() => {
          setInfo(null);
          setError('');
        }}
      />
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 py-16 space-y-6">
      <button onClick={onExit} className="flex items-center gap-1.5 text-ink-400 hover:text-ink-100 text-sm">
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>
      <div className="surface-raised p-6 sm:p-8 space-y-5">
        <div className="flex items-center gap-3">
          <span className="p-2.5 rounded-xl bg-sky-500/15 text-sky-400">
            <FileText className="w-6 h-6" />
          </span>
          <h1 className="text-2xl font-display font-bold">Entrar a un examen</h1>
        </div>
        <p className="text-ink-300 text-sm">Escribe el PIN de 6 dígitos que te dio quien preparó el examen.</p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void lookup(code);
          }}
          className="space-y-3"
        >
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="PIN"
            aria-label="PIN del examen"
            inputMode="numeric"
            autoFocus
            autoComplete="off"
            spellCheck={false}
            className="w-full px-4 py-4 rounded-xl text-center text-2xl font-mono font-bold tracking-[0.3em]
                       bg-ink-850 border-2 border-ink-700 text-ink-50 outline-none focus:border-sky-500
                       placeholder:tracking-normal placeholder:font-sans placeholder:font-normal placeholder:text-lg placeholder:text-ink-500"
          />
          {error && <p className="text-danger-400 text-sm text-center">{error}</p>}
          <button
            type="submit"
            disabled={busy || code.length !== 6}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold bg-ember-500 hover:bg-ember-400
                       text-on-accent disabled:bg-ink-800 disabled:text-ink-500 transition-colors"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
            Continuar
          </button>
        </form>
      </div>
    </div>
  );
};

/* ================================================================
 * Ficha y nombre
 * ================================================================ */

const ExamEntry: React.FC<{
  info: ExamPublicInfo;
  busy: boolean;
  error: string;
  defaultName?: { firstName: string; lastName: string } | null;
  onBegin: (firstName: string, lastName: string, consent: boolean) => void;
  onBack: () => void;
}> = ({ info, busy, error, defaultName, onBegin, onBack }) => {
  const [firstName, setFirstName] = useState(defaultName?.firstName ?? '');
  const [lastName, setLastName] = useState(defaultName?.lastName ?? '');
  const [askConsent, setAskConsent] = useState(false);

  if (askConsent) {
    return (
      <MonitorConsent
        exam
        hostName={info.creatorName || 'Quien creó el examen'}
        onAccept={() => onBegin(firstName.trim(), lastName.trim(), true)}
        onLeave={() => setAskConsent(false)}
      />
    );
  }

  const ready = firstName.trim().length >= 2 && lastName.trim().length >= 2;

  return (
    <div className="max-w-lg mx-auto px-4 py-12 space-y-6">
      <button onClick={onBack} className="flex items-center gap-1.5 text-ink-400 hover:text-ink-100 text-sm">
        <ArrowLeft className="w-4 h-4" /> Otro PIN
      </button>
      <div className="surface-raised p-6 sm:p-8 space-y-5">
        <div>
          <p className="text-ink-500 text-xs uppercase tracking-wider">Examen · PIN {info.code}</p>
          <h1 className="text-2xl font-display font-bold mt-1">{info.title}</h1>
          {info.creatorName && <p className="text-ink-400 text-sm mt-1">Preparado por {info.creatorName}</p>}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-ink-850 border border-ink-700 p-3">
            <p className="text-ink-500 text-xs">Preguntas</p>
            <p className="text-xl font-bold">{info.questionCount}</p>
          </div>
          <div className="rounded-xl bg-ink-850 border border-ink-700 p-3">
            <p className="text-ink-500 text-xs">Tiempo</p>
            <p className="text-xl font-bold">{formatMinutes(info.durationMin)}</p>
          </div>
        </div>

        <ul className="text-sm text-ink-300 space-y-1.5 list-disc pl-5">
          <li>Puedes ir a cualquier pregunta, volver atrás y cambiar tus respuestas.</li>
          <li>El reloj empieza al pulsar «Empezar» y no se detiene. Al llegar a cero se entrega solo.</li>
          <li>No se puede copiar el texto. Se registra si sales de esta pestaña, copias o pegas.</li>
          {info.monitor && <li>Se grabará tu pantalla de Open Dahu durante el examen (te pediremos permiso).</li>}
        </ul>

        {!info.isOpen ? (
          <p className="rounded-xl border border-danger-500/40 bg-danger-500/10 text-danger-400 p-3 text-sm">
            Este examen está cerrado y ya no admite a nadie.
          </p>
        ) : (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!ready || busy) return;
              if (info.monitor) setAskConsent(true);
              else onBegin(firstName.trim(), lastName.trim(), false);
            }}
          >
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="space-y-1">
                <span className="text-sm text-ink-300">Nombre</span>
                <input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value.slice(0, 40))}
                  autoComplete="given-name"
                  className="w-full h-11 px-3 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 outline-none focus:border-sky-500"
                />
              </label>
              <label className="space-y-1">
                <span className="text-sm text-ink-300">Apellido</span>
                <input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value.slice(0, 40))}
                  autoComplete="family-name"
                  className="w-full h-11 px-3 rounded-lg bg-ink-850 border border-ink-700 text-ink-100 outline-none focus:border-sky-500"
                />
              </label>
            </div>
            {error && <p className="text-danger-400 text-sm">{error}</p>}
            <button
              type="submit"
              disabled={!ready || busy}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold bg-ember-500 hover:bg-ember-400
                         text-on-accent disabled:bg-ink-800 disabled:text-ink-500 transition-colors"
            >
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Clock className="w-5 h-5" />}
              Empezar el examen
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

/* ================================================================
 * Examen en curso
 * ================================================================ */

type SaveStatus = 'idle' | 'saving' | 'saved' | 'offline';

const ExamRunner: React.FC<{
  attempt: SavedAttempt;
  initial: ExamState;
  onFinished: (s: ExamState) => void;
}> = ({ attempt, initial, onFinished }) => {
  const questions = initial.questions;
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, AnswerPayload>>(initial.answers);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Preguntas marcadas para revisar: ayuda personal, solo en este navegador.
  const flagKey = `dahu_exam_flags_${initial.attemptId}`;
  const [flagged, setFlagged] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(flagKey) ?? '[]') as string[]);
    } catch {
      return new Set();
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(flagKey, JSON.stringify([...flagged]));
    } catch {
      /* sin almacenamiento */
    }
  }, [flagKey, flagged]);

  /* ---------------- reloj del servidor ---------------- */

  const offset = useRef(initial.serverNow - Date.now());
  const [now, setNow] = useState(() => Date.now() + offset.current);
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now() + offset.current), 250);
    return () => window.clearInterval(id);
  }, []);
  const msLeft = Math.max(0, initial.deadlineAt - now);

  /* ---------------- guardado ---------------- */

  // Lo que falta por llegar al servidor, por pregunta. Si falla la red se
  // reintenta; si se cambia otra vez antes, manda la última versión.
  const pending = useRef(new Map<string, AnswerPayload | null>());
  const timers = useRef(new Map<string, number>());
  const inFlight = useRef<Promise<void> | null>(null);

  const pushPending = useCallback(async () => {
    if (inFlight.current) return inFlight.current;
    const run = (async () => {
      while (pending.current.size > 0) {
        const [qid, payload] = pending.current.entries().next().value as [string, AnswerPayload | null];
        pending.current.delete(qid);
        setSaveStatus('saving');
        try {
          await examApi.answer(attempt, qid, payload);
        } catch (err) {
          if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
            // Tiempo agotado o examen cerrado: no tiene sentido reintentar.
            continue;
          }
          if (!pending.current.has(qid)) pending.current.set(qid, payload);
          setSaveStatus('offline');
          return;
        }
      }
      setSaveStatus('saved');
    })();
    inFlight.current = run;
    try {
      await run;
    } finally {
      inFlight.current = null;
    }
  }, [attempt]);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (pending.current.size > 0) void pushPending();
    }, 3000);
    return () => window.clearInterval(id);
  }, [pushPending]);

  const setAnswer = (qid: string, payload: AnswerPayload | null, delay = 0) => {
    setAnswers((prev) => {
      const next = { ...prev };
      if (payload) next[qid] = payload;
      else delete next[qid];
      return next;
    });
    const old = timers.current.get(qid);
    if (old) window.clearTimeout(old);
    const queue = () => {
      timers.current.delete(qid);
      pending.current.set(qid, payload);
      void pushPending();
    };
    if (delay > 0) timers.current.set(qid, window.setTimeout(queue, delay));
    else queue();
  };

  /** Manda ya lo que esté esperando su pausa de escritura. */
  const flushAll = async () => {
    for (const [qid, t] of timers.current) {
      window.clearTimeout(t);
      timers.current.delete(qid);
      const a = answersRef.current[qid];
      pending.current.set(qid, a ?? null);
    }
    await pushPending();
  };
  const answersRef = useRef(answers);
  answersRef.current = answers;

  /* ---------------- entrega ---------------- */

  const submit = useCallback(async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      await flushAll();
      stopMonitor.current?.();
      stopMonitor.current = null;
      onFinished(await examApi.submit(attempt));
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'No se pudo entregar. Revisa tu conexión.');
      setSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt, onFinished]);

  // Al llegar a cero se entrega solo. Si no hay red, el servidor lo cerrará
  // igual con lo que ya tenía guardado.
  const autoSubmitted = useRef(false);
  useEffect(() => {
    if (msLeft === 0 && !autoSubmitted.current) {
      autoSubmitted.current = true;
      void submit();
    }
  }, [msLeft, submit]);

  /* ---------------- antitrampas ---------------- */

  const stopMonitor = useRef<(() => void) | null>(null);
  useEffect(() => {
    const transport = examTransport(attempt);
    // Con grabación, el grabador ya cuenta las señales; sin ella, se cuentan igual.
    const stop = initial.recorded ? startRecording(transport) : watchCheatSignals(transport.sendFlag);
    stopMonitor.current = stop;

    // Como en las partidas, el texto no se puede seleccionar (`no-select-game`);
    // aquí además se bloquea copiar, cortar y el menú contextual.
    const block = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (e.type !== 'copy' && target?.closest('input, textarea')) return;
      e.preventDefault();
    };
    document.addEventListener('copy', block);
    document.addEventListener('cut', block);
    document.addEventListener('contextmenu', block);
    document.addEventListener('dragstart', block);

    // Avisa antes de cerrar o recargar: no se pierde nada, pero el reloj sigue.
    const beforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', beforeUnload);

    return () => {
      stop();
      document.removeEventListener('copy', block);
      document.removeEventListener('cut', block);
      document.removeEventListener('contextmenu', block);
      document.removeEventListener('dragstart', block);
      window.removeEventListener('beforeunload', beforeUnload);
    };
  }, [attempt, initial.recorded]);

  /* ---------------- pantalla ---------------- */

  const question = questions[index];
  const answeredCount = questions.filter((q) => answers[q.id]).length;
  const unanswered = questions.length - answeredCount;
  const urgent = msLeft <= 60_000;

  const current = question ? answers[question.id] : undefined;
  const choice = current?.kind === 'choice' ? current.optionId : undefined;
  const multi = current?.kind === 'multi' ? current.optionIds : [];
  const text = current?.kind === 'text' ? current.text : '';
  const links: PairLink[] = current?.kind === 'pairs' ? current.links : [];

  return (
    <div className="no-select-game min-h-screen flex flex-col">
      {/* Barra fija: reloj y entrega siempre a la vista. */}
      <header className="sticky top-0 z-30 border-b border-ink-800 bg-ink-950/90 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 h-16 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="font-bold truncate">{initial.title}</p>
            <p className="text-ink-500 text-xs truncate">
              {initial.firstName} {initial.lastName} · <SaveBadge status={saveStatus} />
            </p>
          </div>
          <div
            role="timer"
            aria-label="Tiempo restante"
            className={`flex items-center gap-1.5 px-3 h-10 rounded-lg font-mono font-bold text-lg tabular-nums border ${
              urgent
                ? 'border-danger-500/60 bg-danger-500/15 text-danger-400 animate-pulse'
                : 'border-ink-700 bg-ink-850 text-ink-100'
            }`}
          >
            <Clock className="w-4 h-4" />
            {formatClock(msLeft)}
          </div>
          <button
            onClick={() => setConfirmSubmit(true)}
            disabled={submitting}
            className="flex items-center gap-1.5 px-3 sm:px-4 h-10 rounded-lg font-bold text-sm bg-ember-500 hover:bg-ember-400 text-on-accent transition-colors"
          >
            <Send className="w-4 h-4" />
            <span className="hidden sm:inline">Entregar</span>
          </button>
        </div>
      </header>

      <div className="max-w-5xl w-full mx-auto px-4 py-5 flex-1 space-y-5">
        <div className="flex items-start gap-2 text-xs text-ink-400 rounded-xl border border-ink-700 bg-ink-850 px-3 py-2">
          {initial.recorded ? <Eye className="w-4 h-4 text-sky-400 shrink-0" /> : <ShieldAlert className="w-4 h-4 text-sky-400 shrink-0" />}
          <span>
            {initial.recorded ? 'Tu pantalla de Open Dahu se está grabando. ' : ''}
            Se registra si sales de esta pestaña, cambias de ventana, copias o pegas.
          </span>
        </div>

        {/* Navegación libre entre preguntas */}
        <nav aria-label="Preguntas" className="flex flex-wrap gap-1.5">
          {questions.map((q, i) => {
            const done = Boolean(answers[q.id]);
            const isFlagged = flagged.has(q.id);
            return (
              <button
                key={q.id}
                onClick={() => setIndex(i)}
                aria-current={i === index ? 'step' : undefined}
                aria-label={`Pregunta ${i + 1}${done ? ', contestada' : ''}${isFlagged ? ', marcada' : ''}`}
                className={`relative w-10 h-10 rounded-lg text-sm font-bold border transition-colors ${
                  i === index
                    ? 'border-sky-500 bg-sky-500/20 text-sky-300'
                    : done
                      ? 'border-success-500/50 bg-success-500/15 text-success-400'
                      : 'border-ink-700 bg-ink-850 text-ink-400 hover:text-ink-100'
                }`}
              >
                {i + 1}
                {isFlagged && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-ember-500" />}
              </button>
            );
          })}
        </nav>

        {question && (
          <section key={question.id} className="space-y-5">
            <div className="flex items-center justify-between gap-3">
              <span className="chip border-ink-700 bg-ink-850 text-ink-300">
                Pregunta {index + 1} de {questions.length}
                {question.pointsMultiplier === 2 ? ' · vale doble' : question.pointsMultiplier === 0 ? ' · sin puntos' : ''}
              </span>
              <button
                onClick={() =>
                  setFlagged((prev) => {
                    const next = new Set(prev);
                    if (next.has(question.id)) next.delete(question.id);
                    else next.add(question.id);
                    return next;
                  })
                }
                className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border transition-colors ${
                  flagged.has(question.id)
                    ? 'border-ember-500/60 bg-ember-500/15 text-ember-300'
                    : 'border-ink-700 text-ink-400 hover:text-ink-100'
                }`}
              >
                <Bookmark className="w-4 h-4" />
                {flagged.has(question.id) ? 'Marcada' : 'Revisar luego'}
              </button>
            </div>

            <h2 className="text-xl md:text-2xl font-bold text-balance leading-snug">{question.title}</h2>
            {question.mediaUrl && (
              <img
                src={question.mediaUrl}
                alt=""
                draggable={false}
                className="max-h-56 mx-auto rounded-2xl border border-ink-700 object-contain"
              />
            )}

            {question.type === 'multiple_choice' && (
              <MultipleChoiceQuestion
                options={question.options ?? []}
                selectedOptionId={choice}
                disabled={submitting}
                onSelect={(id) => setAnswer(question.id, { kind: 'choice', optionId: id })}
              />
            )}
            {question.type === 'true_false' && (
              <TrueFalseQuestion
                options={question.options ?? []}
                selectedOptionId={choice}
                disabled={submitting}
                onSelect={(id) => setAnswer(question.id, { kind: 'choice', optionId: id })}
              />
            )}
            {question.type === 'multi_select' && (
              <MultiSelectQuestion
                options={question.options ?? []}
                selectedIds={multi}
                disabled={submitting}
                hideSubmit
                onToggle={(id) => {
                  const next = multi.includes(id) ? multi.filter((x) => x !== id) : [...multi, id];
                  setAnswer(question.id, next.length ? { kind: 'multi', optionIds: next } : null, 400);
                }}
                onSubmit={() => undefined}
              />
            )}
            {question.type === 'fill_blank' && (
              <FillBlankQuestion
                value={text}
                disabled={submitting}
                hideSubmit
                onChange={(value) => setAnswer(question.id, value.trim() ? { kind: 'text', text: value } : null, 700)}
                onSubmit={() => undefined}
              />
            )}
            {question.type === 'match_pairs' && (
              <MatchPairsQuestion
                leftItems={question.leftItems ?? []}
                rightItems={question.rightItems ?? []}
                links={links}
                disabled={submitting}
                hideSubmit
                onLink={(leftId, rightId) =>
                  setAnswer(question.id, {
                    kind: 'pairs',
                    links: [...links.filter((l) => l.leftId !== leftId && l.rightId !== rightId), { leftId, rightId }],
                  })
                }
                onUnlink={(leftId) => {
                  const next = links.filter((l) => l.leftId !== leftId);
                  setAnswer(question.id, next.length ? { kind: 'pairs', links: next } : null);
                }}
                onSubmit={() => undefined}
              />
            )}

            {current && question.type !== 'fill_blank' && (
              <div className="flex justify-center">
                <button
                  onClick={() => setAnswer(question.id, null)}
                  className="text-sm text-ink-500 hover:text-ink-200 underline underline-offset-4"
                >
                  Borrar mi respuesta
                </button>
              </div>
            )}
          </section>
        )}

        <div className="flex items-center justify-between gap-3 pt-2">
          <button
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            disabled={index === 0}
            className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-100 disabled:opacity-40 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Anterior
          </button>
          <span className="text-ink-500 text-sm">
            {answeredCount} de {questions.length} contestadas
          </span>
          {index + 1 < questions.length ? (
            <button
              onClick={() => setIndex((i) => Math.min(questions.length - 1, i + 1))}
              className="flex items-center gap-2 px-5 py-3 rounded-xl font-bold bg-sky-600 hover:bg-sky-500 text-white transition-colors"
            >
              Siguiente <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={() => setConfirmSubmit(true)}
              className="flex items-center gap-2 px-5 py-3 rounded-xl font-bold bg-ember-500 hover:bg-ember-400 text-on-accent transition-colors"
            >
              Entregar <Send className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {(confirmSubmit || (submitting && msLeft === 0)) && (
        <div className="fixed inset-0 z-50 bg-ink-950/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="surface-raised max-w-sm w-full p-6 space-y-4 animate-pop">
            {msLeft === 0 ? (
              <>
                <h2 className="text-xl font-bold">Se acabó el tiempo</h2>
                <p className="text-ink-300 text-sm">Entregando tus respuestas…</p>
              </>
            ) : (
              <>
                <h2 className="text-xl font-bold">¿Entregar el examen?</h2>
                <p className="text-ink-300 text-sm">
                  {unanswered > 0
                    ? `Te quedan ${unanswered} ${unanswered === 1 ? 'pregunta' : 'preguntas'} sin contestar. `
                    : 'Has contestado todas las preguntas. '}
                  Después de entregar ya no podrás cambiar nada.
                </p>
              </>
            )}
            {submitError && <p className="text-danger-400 text-sm">{submitError}</p>}
            <div className="flex gap-3">
              {msLeft > 0 && (
                <button
                  onClick={() => setConfirmSubmit(false)}
                  disabled={submitting}
                  className="flex-1 px-4 py-3 rounded-xl font-semibold bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-100"
                >
                  Seguir
                </button>
              )}
              <button
                onClick={() => void submit()}
                disabled={submitting}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-bold bg-ember-500 hover:bg-ember-400 text-on-accent"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submitError ? 'Reintentar' : 'Entregar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SaveBadge: React.FC<{ status: SaveStatus }> = ({ status }) => {
  if (status === 'saving') return <span>Guardando…</span>;
  if (status === 'offline')
    return (
      <span className="text-danger-400 inline-flex items-center gap-1">
        <WifiOff className="w-3 h-3" /> Sin conexión, reintentando
      </span>
    );
  if (status === 'saved') return <span className="text-success-400">Guardado</span>;
  return <span>Las respuestas se guardan solas</span>;
};

/* ================================================================
 * Final
 * ================================================================ */

const ExamFinished: React.FC<{ state: ExamState; onExit: () => void }> = ({ state, onExit }) => {
  const percent = useMemo(
    () => (state.result && state.result.maxScore > 0 ? Math.round((state.result.score / state.result.maxScore) * 100) : 0),
    [state.result],
  );
  return (
    <div className="max-w-md mx-auto px-4 py-16 text-center space-y-6">
      <CheckCircle2 className="w-14 h-14 mx-auto text-success-400" />
      <div>
        <h1 className="text-3xl font-display font-bold">
          {state.finishReason === 'expired' ? 'Se acabó el tiempo' : 'Examen entregado'}
        </h1>
        <p className="text-ink-400 mt-2">{state.title}</p>
        <p className="text-ink-500 text-sm">
          {state.firstName} {state.lastName}
        </p>
      </div>
      {state.result ? (
        <div className="surface-raised p-6">
          <p className="text-ink-400 text-sm">Tu nota</p>
          <p className="text-5xl font-display font-black text-brand-gradient">
            {formatGrade(state.result.grade)}
            <span className="text-2xl text-ink-400"> / {state.result.gradeScale}</span>
          </p>
          <p className="text-ink-400 mt-1 text-sm">
            {formatGrade(state.result.score)} de {state.result.maxScore} puntos · {percent}%
          </p>
        </div>
      ) : (
        <p className="surface p-5 text-ink-300 text-sm">
          Tus respuestas quedaron guardadas. Quien preparó el examen te dará la nota.
        </p>
      )}
      <button
        onClick={onExit}
        className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-100"
      >
        <LogOut className="w-4 h-4" /> Salir
      </button>
    </div>
  );
};

/* ================================================================ */

export function formatClock(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function formatMinutes(min: number): string {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

export function formatGrade(n: number): string {
  return n.toLocaleString('es', { maximumFractionDigits: 2 });
}
